import { useState, useEffect } from "react";
import {
  Button, TextField, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Menu,
  MenuItem, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase-config";
import { collection, getDocs, updateDoc, doc, setDoc, serverTimestamp, query, where } from "firebase/firestore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { v4 as uuidv4 } from "uuid";
import "../style/Global.css";
import "../style/QuestionnaireView.css";
import CircularProgress from "@mui/material/CircularProgress";

function ViewQuestionnaire() {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuQuestionnaire, setSelectedMenuQuestionnaire] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classSearch, setClassSearch] = useState("");
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const navigate = useNavigate();

  const fetchQuestionnaires = async (user) => {
    try {
      const now = new Date();

      // 1. Get all active/inactive questionnaires
      const snapshot = await getDocs(
        query(collection(db, "Questionnaires"), where("status", "in", ["active", "inactive"]))
      );

      const questionnairesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        title: doc.data().title || "Untitled"
      }));

      // 2. Get class ownership: classes created by current user
      const classSnap = await getDocs(
        query(collection(db, "Classes"), where("teacherId", "==", user.uid))
      );
      const ownedClassIds = new Set(classSnap.docs.map(doc => doc.data().classId));

      // 3. Filter only questionnaires that belong to user's classes
      const filteredQuestionnaires = questionnairesData.filter(q =>
        ownedClassIds.has(q.classId)
      );

      setQuestionnaires(filteredQuestionnaires);

      // 4. Auto-activate eligible inactive ones
      filteredQuestionnaires.forEach(async (q) => {
        if (q.status === "inactive" && q.startDate && q.endDate) {
          const start = new Date(q.startDate);
          const end = new Date(q.endDate);
          if (start <= now && now <= end) {
            await updateDoc(doc(db, "Questionnaires", q.id), { status: "active" });
          }
        }
      });

    } catch (err) {
      console.error("Error fetching questionnaires:", err);
      setQuestionnaires([]);
    }
  };

  const fetchClasses = async (user) => {
      setIsLoadingClasses(true);
      try {
        if (!user) return;

        const snapshot = await getDocs(
          query(collection(db, "Classes"), where("teacherId", "==", user.uid))
        );

        const allUserClasses = snapshot.docs
          .map(doc => doc.data())
          .filter(data => data.isArchived !== true) 
          .map(data => {
            return {
              classId: data.classId,
              className: data.className || data.name || "Unnamed",
              hasQuestionnaire: false,
            };
          });

        const qSnapshot = await getDocs(collection(db, "Questionnaires"));
        const qData = qSnapshot.docs.map(doc => doc.data());

        const takenClassIds = new Set(
          qData
            .filter(q => ["active", "inactive"].includes(q.status))
            .map(q => q.classId)
        );

        const classesWithStatus = allUserClasses.map(c => ({
          ...c,
          hasQuestionnaire: takenClassIds.has(c.classId),
        }));

        setClasses(classesWithStatus);
      } catch (err) {
        console.error("Error loading classes:", err);
        setClasses([]);
      } finally {
        setIsLoadingClasses(false);
      }
    };

  useEffect(() => {
  const unsubscribe = auth.onAuthStateChanged((user) => {
    if (user) {
      fetchQuestionnaires(user);
      fetchClasses(user); 
    }
  });
  return () => unsubscribe();
}, []);


  const handleClickMenu = (e, q) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedMenuQuestionnaire(q);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const handleOpenDeleteConfirm = () => {
    setQuestionnaireToDelete(selectedMenuQuestionnaire);
    handleCloseMenu();
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => setDeleteConfirmOpen(false);

  const handleConfirmDelete = async () => {
    if (questionnaireToDelete) {
      try {
        await updateDoc(doc(db, "Questionnaires", questionnaireToDelete.id), { status: "deleted" });

        await fetchQuestionnaires(auth.currentUser);
        await fetchClasses(auth.currentUser);

        setDeleteConfirmOpen(false);
        setQuestionnaireToDelete(null);
        alert("Questionnaire deleted successfully");
      } catch (err) {
        console.error("Error deleting questionnaire:", err);
      }
    }
  };

  const handleOpenDuplicateDialog = () => {
    setSelectedClasses([]);
    setDuplicateDialogOpen(true);
    handleCloseMenu();
  };

  const handleDuplicateConfirm = async () => {
    try {
      const validClasses = selectedClasses.filter(className => className !== "Unknown");
      if (!selectedMenuQuestionnaire || validClasses.length === 0) return;
  
      const classSnapshot = await getDocs(
        query(collection(db, "Classes"), where("className", "in", validClasses))
      );
  
      const classIdMap = {};
      classSnapshot.forEach(doc => {
        const classData = doc.data();
        classIdMap[classData.className || classData.name] = classData.classId;
      });
  
      const promises = validClasses.map(className => {
        const classId = classIdMap[className];
        if (!classId) return Promise.resolve();
  
        const newQuestionnaireId = uuidv4();
        const {
          id: _originalDocId,
          questionnaireId: originalQuestionnaireId,
          title,
          ...rest
        } = selectedMenuQuestionnaire;
  
        return setDoc(doc(db, "Questionnaires", newQuestionnaireId), {
          ...rest,
          classId,
          className,
          title: `${title} (Copy)`,
          questionnaireId: newQuestionnaireId,
          copiedFrom: originalQuestionnaireId || _originalDocId, // fallback to doc.id if missing
          createdAt: serverTimestamp(),
          status: "inactive"
        });
      });
  
      await Promise.all(promises);

      await fetchQuestionnaires(auth.currentUser);
      await fetchClasses(auth.currentUser);
  
      alert("Questionnaire duplicated successfully!");
      setDuplicateDialogOpen(false);
      setSelectedClasses([]);
    } catch (err) {
      console.error("Duplication failed:", err);
      alert("Error duplicating questionnaire. Please try again.");
    }
  };

  const filterQuestionnaires = (list, term) => {
    return list.filter(q => 
      q.title?.toLowerCase().includes(term.toLowerCase()) && q.title !== "Untitled"
    );
  };

  return (
    <div className="view-container">
      <div className="header-with-button">
        <h2>Questionnaire List</h2>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/CreateQuestionnaire")}
          className="create-button"
        >
          Create Questionnaire
        </Button>
      </div>

      <TextField
        fullWidth
        label="Search questionnaires"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        margin="normal"
        size="small"
      />

        <TableContainer component={Paper} className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created On</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterQuestionnaires(questionnaires, searchTerm).map(q => (
              <TableRow
              key={q.id}
              onClick={() => navigate(`/Questionnaire/${q.id}`)}
              className="clickable-row"
              hover
              >
                <TableCell>
                  <div className="questionnaire-title">{q.title}</div>
                  {q.description && (
                    <div className="questionnaire-description">
                      {q.description.length > 50 
                        ? `${q.description.substring(0, 50)}...` 
                        : q.description}
                    </div>
                  )}
                </TableCell>
                <TableCell>{q.className || "N/A"}</TableCell>
                <TableCell>
                  <span className={`status-badge status-${q.status}`}>
                    {q.status}
                  </span>
                </TableCell>
                <TableCell>
                  {q.createdAt?.seconds
                    ? new Date(q.createdAt.seconds * 1000).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric"
                    })
                    : "N/A"}
                </TableCell>
                <TableCell className="table-cell-right">
                  <IconButton onClick={(e) => handleClickMenu(e, q)} size="small">
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {questionnaires.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <div className="emtpy-state">
                    {searchTerm ? "No matching questionnaires found" : "No questionnaires created yet"}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleOpenDuplicateDialog}>Duplicate</MenuItem>
        <MenuItem onClick={() => {
          navigate(`/EditQuestionnaire/${selectedMenuQuestionnaire.id}`);
          handleCloseMenu();
        }}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteConfirm}>Delete</MenuItem>
      </Menu>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '15px'
          }
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the questionnaire "{questionnaireToDelete?.title}"?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '15px'
          }
        }}
      >
        <DialogTitle>Duplicate to Classes</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search your classes..."
            value={classSearch}
            onChange={(e) => setClassSearch(e.target.value)}
            margin="normal"
            size="small"
          />
          <div className="duplicate-class-list">
            {isLoadingClasses ? (
              <div className="loading-state">
                <CircularProgress size={24} />
                <span>Loading classes...</span>
              </div>
            ) : classes.length === 0 ? (
              <p>No classes found. Please create classes first.</p>
            ) : (
              classes
              .filter(c => c.className.toLowerCase().includes(classSearch.toLowerCase()))
              .map((cls, index) => {
                const disabled = cls.hasQuestionnaire;
                return (
                  <div key={index} className={`class-checkbox ${disabled ? "disabled" : ""}`}>
                    <input
                      type="checkbox"
                      id={`class-${index}`}
                      checked={selectedClasses.includes(cls.className)}
                      onChange={() => {
                        setSelectedClasses(prev =>
                          prev.includes(cls.className)
                            ? prev.filter(item => item !== cls.className)
                            : [...prev, cls.className]
                        );
                      }}
                      disabled={disabled}
                    />
                    <label htmlFor={`class-${index}`}>{cls.className}</label>
                    {disabled && (
                      <span className="class-warning">
                        ⚠ Already has questionnaire
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={selectedClasses.length === 0 || isLoadingClasses}
            onClick={handleDuplicateConfirm}
          >
            Duplicate to {selectedClasses.length} Class{selectedClasses.length !== 1 ? 'es' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ViewQuestionnaire;